import { useState } from "react";
import { useSession } from "../state";

const REASONS = [
  "minor depicted",
  "non-consensual content",
  "sexual / explicit content",
  "copyright infringement",
  "harassment / hate",
  "other",
];

export function Complaint() {
  const { session, token } = useSession();
  const [reason, setReason] = useState(REASONS[0]);
  const [url, setUrl] = useState("");
  const [body, setBody] = useState("");
  const [contact, setContact] = useState(session?.email || "");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const submit = async () => {
    setBusy(true); setErr(null);
    try {
      const t = await token();
      const r = await fetch(import.meta.env.VITE_API_BASE + "/complaints", {
        method: "POST",
        headers: {
          "content-type": "application/json",
          ...(t ? { authorization: `Bearer ${t}` } : {}),
        },
        body: JSON.stringify({ reason, related_url: url, body, contact }),
      });
      if (!r.ok) throw new Error(`${r.status} ${await r.text()}`);
      setDone(true);
    } catch (e: any) {
      setErr(e.message || "submission failed");
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="mt-16 text-center max-w-md mx-auto">
        <h2 className="font-display font-black text-3xl mb-3">thank you</h2>
        <p className="text-ink-soft">
          your report has been received. we review every report; you may hear back at the contact you provided.
        </p>
      </div>
    );
  }

  return (
    <article className="mt-6 max-w-2xl">
      <h2 className="font-display font-black text-3xl mb-2">report content</h2>
      <p className="text-ink-soft mb-6">
        if you believe content on thelivephotos violates our terms or your rights, tell us about it. we read every report
        and act on those that warrant it.
      </p>

      <label className="block mb-3">
        <span className="text-sm text-ink-soft">reason</span>
        <select
          className="input mt-1"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        >
          {REASONS.map((r) => (<option key={r} value={r}>{r}</option>))}
        </select>
      </label>

      <label className="block mb-3">
        <span className="text-sm text-ink-soft">link to the content (optional)</span>
        <input
          type="url"
          className="input mt-1"
          placeholder="https://app.thelivephotos.com/jobs/…"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
        />
      </label>

      <label className="block mb-3">
        <span className="text-sm text-ink-soft">contact email (so we can follow up)</span>
        <input
          type="email"
          className="input mt-1"
          placeholder="you@example.com"
          value={contact}
          onChange={(e) => setContact(e.target.value)}
        />
      </label>

      <label className="block mb-4">
        <span className="text-sm text-ink-soft">describe the issue</span>
        <textarea
          className="textarea mt-1 h-40 resize-none"
          maxLength={4000}
          value={body}
          onChange={(e) => setBody(e.target.value)}
        />
      </label>

      <button
        className="btn-primary"
        disabled={busy || !body.trim()}
        onClick={submit}
      >
        {busy ? "sending…" : "send report"}
      </button>
      {err && <p className="text-coral-dark mt-3 text-sm">{err}</p>}

      <div className="text-xs text-ink-mute mt-8 space-y-1">
        <p>for urgent CSAM concerns, please also report to:</p>
        <ul className="list-disc pl-5 space-y-0.5">
          <li>
            <a className="underline" href="https://www.iwf.org.uk/report" target="_blank" rel="noreferrer">UK Internet Watch Foundation</a>
          </li>
          <li>
            <a className="underline" href="https://www.inhope.org/EN" target="_blank" rel="noreferrer">INHOPE</a> (network of EU hotlines — find your country)
          </li>
          <li>
            <a className="underline" href="https://www.missingkids.org/gethelpnow/cybertipline" target="_blank" rel="noreferrer">NCMEC CyberTipline</a> (US/global)
          </li>
        </ul>
        <p className="mt-3">
          For data-protection complaints, you may also contact your national DPA — UK users: the
          {" "}<a className="underline" href="https://ico.org.uk/make-a-complaint/" target="_blank" rel="noreferrer">ICO</a>.
        </p>
      </div>
    </article>
  );
}
